import { cn } from "@gettactic/helpers/src/css/cn";
import { mergeRefs } from "@gettactic/helpers/src/dom/mergeRef";
import React from "react";
import { FormControlWrapper } from "../FormControlWrapper";
import { FormControlOutline } from "../FormControlWrapper/FormControlOutline";
import {
  useFormControlInputA11y,
  useFormControlWrapper
} from "../FormControlWrapper/FormControlWrapperProvider";
import {
  disabledCssClasses,
  disabledOutlineClasses
} from "../FormControlWrapper/defines";
import { splitFormControlWrapperProps } from "../FormControlWrapper/helpers";
import {
  NumberInputProps,
  NumberInputPropsWithFormControl
} from "./NumberInput.types";

export const NumberInputInput = React.forwardRef<
  HTMLInputElement,
  NumberInputProps
>((props, ref) => {
  const {
    inputClassName,
    className,
    disableOutline,
    unit,
    unitElement,
    ...rest
  } = props;
  const { id, isDisabled } = useFormControlWrapper();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const hasUnit = Boolean(unit || unitElement);
  const unitId = `${id}-unit`;
  // The unit is part of the value ("15" means "15 mins"), so announce it with the field
  const a11yProps = useFormControlInputA11y({
    "aria-invalid": rest["aria-invalid"] === true || undefined,
    "aria-required": rest["aria-required"] === true || undefined,
    "aria-describedby":
      [rest["aria-describedby"], hasUnit ? unitId : undefined]
        .filter(Boolean)
        .join(" ") || undefined
  });

  return (
    <div className="flex w-full items-center">
      <input
        {...rest}
        type="number"
        className={cn(
          "flex h-8 w-full border-0 bg-transparent text-sm shadow-none outline-none ring-0 transition-colors placeholder:text-muted-foreground",
          disabledCssClasses,
          disabledOutlineClasses,
          inputClassName,
          className
        )}
        style={{
          borderWidth: 0,
          outline: 0,
          border: 0,
          boxShadow: "none",
          ...rest.style
        }}
        {...a11yProps}
        ref={mergeRefs([ref, inputRef])}
        id={id}
        disabled={isDisabled || rest.disabled}
      />
      {hasUnit && (
        <span
          id={unitId}
          className="pointer-events-none flex shrink-0 items-center px-2 text-sm font-medium text-content"
        >
          {unitElement || unit}
        </span>
      )}
    </div>
  );
});

NumberInputInput.displayName = "NumberInputInput";

/**
 * A numeric field with an optional unit suffix ("mins", "days"), announced with the value.
 * Inside react-hook-form use `HookFormNumberInput`, which also validates `min` / `max`.
 */
export const NumberInput = React.forwardRef<
  HTMLInputElement,
  NumberInputPropsWithFormControl
>((props, ref) => {
  const [wrapperProps, inputProps] = splitFormControlWrapperProps(props);

  return (
    <FormControlWrapper
      enableSlotsInputFocus
      {...wrapperProps}
      isDisabled={wrapperProps.isDisabled || inputProps.disabled}
    >
      <NumberInputInput ref={ref} {...inputProps} />
      {inputProps.disableOutline !== true && <FormControlOutline />}
    </FormControlWrapper>
  );
});

NumberInput.displayName = "NumberInput";
